import type { ReceivedEvent } from "@beacon/client-core";
import { RawReportStore, depthBudget, type RawDepth } from "../screens/now/raw-report-model.js";

interface Sub extends AsyncIterable<ReceivedEvent> { close(): Promise<void>; }
type SubClient = { subscribe(filter: { cells: string[]; types: string[]; sinceSeq?: number }): Sub };

export interface RawReportLayerOpts {
  client: SubClient;
  store: RawReportStore;
  /** Reads the relay head seq (Task 2 relayHeadSeq bound to the relay base url). */
  headSeq: () => Promise<number>;
  /** Called after events are folded so the caller can re-render the map source. */
  onChange: () => void;
}

/** Owns the opt-in raw-report layer's subscription lifecycle. View-scoped: activate(cells)
 *  (re-)opens a bounded-backfill+live app.aid.report sub for those cells; deactivate closes
 *  it and clears the store. Never sinceSeq:0 — the backfill is head − depthBudget (clamped),
 *  and "off" depth is live-only (no sinceSeq). */
export class RawReportLayer {
  #client: SubClient;
  #store: RawReportStore;
  #headSeq: () => Promise<number>;
  #onChange: () => void;
  #depth: RawDepth = "medium";
  #sub: Sub | undefined;
  #gen = 0; // guards against a slow activate resolving after a newer activate/deactivate

  constructor(opts: RawReportLayerOpts) {
    this.#client = opts.client;
    this.#store = opts.store;
    this.#headSeq = opts.headSeq;
    this.#onChange = opts.onChange;
  }

  get active(): boolean { return this.#sub !== undefined; }
  setDepth(d: RawDepth): void { this.#depth = d; }

  async activate(cells: string[]): Promise<void> {
    const gen = ++this.#gen;
    this.#closeSub();
    this.#store.clear();
    this.#onChange();
    const budget = depthBudget(this.#depth);
    let sinceSeq: number | undefined;
    if (budget > 0) {
      const head = await this.#headSeq();
      if (gen !== this.#gen) return; // superseded while fetching head
      sinceSeq = Math.max(0, head - budget);
    }
    const filter = sinceSeq === undefined
      ? { cells, types: ["app.aid.report"] }
      : { cells, types: ["app.aid.report"], sinceSeq };
    const sub = this.#client.subscribe(filter);
    this.#sub = sub;
    void this.#pump(sub, gen);
  }

  deactivate(): void {
    this.#gen++;
    this.#closeSub();
    this.#store.clear();
    this.#onChange();
  }

  async #pump(sub: Sub, gen: number): Promise<void> {
    try {
      for await (const evt of sub) {
        if (gen !== this.#gen) break;
        if (this.#store.ingest({ seq: (evt as { seq: number }).seq, json: (evt as { json: Record<string, unknown> }).json })) {
          this.#onChange();
        }
      }
    } catch { /* stream ended/errored — layer simply stops receiving */ }
  }

  #closeSub(): void {
    if (this.#sub) { void Promise.resolve(this.#sub.close()).catch(() => {}); this.#sub = undefined; }
  }
}
